import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Phone, Mail, Instagram, Facebook, Twitter, MoreVertical, Trash2, Edit, Play, Pause, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface RecipientListProps {
  isEnabled: boolean;
}

const contactIcons: Record<string, React.ElementType> = {
  phone: Phone,
  email: Mail,
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
};

const frequencyLabels: Record<string, { en: string; es: string }> = {
  daily: { en: 'Daily', es: 'Diario' },
  weekly: { en: 'Weekly', es: 'Semanal' },
  biweekly: { en: 'Bi-weekly', es: 'Quincenal' },
  monthly: { en: 'Monthly', es: 'Mensual' },
};

export function RecipientList({ isEnabled }: RecipientListProps) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const content = {
    en: {
      noRecipients: 'No recipients added yet',
      noRecipientsDesc: 'Add a recipient to start sending AI-generated announcements',
      nextSend: 'Next send',
      generateNow: 'Generate Now',
      pause: 'Pause',
      resume: 'Resume',
      delete: 'Delete',
      paused: 'Paused',
      active: 'Active',
    },
    es: {
      noRecipients: 'No hay destinatarios aún',
      noRecipientsDesc: 'Agrega un destinatario para comenzar a enviar anuncios generados por IA',
      nextSend: 'Próximo envío',
      generateNow: 'Generar Ahora',
      pause: 'Pausar',
      resume: 'Reanudar',
      delete: 'Eliminar',
      paused: 'Pausado',
      active: 'Activo',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  const { data: recipients, isLoading } = useQuery({
    queryKey: ['auto-compose-recipients', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('auto_compose_recipients')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('auto_compose_recipients')
        .update({ is_active: isActive })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-compose-recipients'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('auto_compose_recipients')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-compose-recipients'] });
      toast.success(({ en: 'Recipient deleted', es: 'Destinatario eliminado', fr: 'Destinataire supprimé', pt: 'Destinatário excluído', ar: 'تم حذف المستلم', ja: '受信者を削除しました', ko: '수신자 삭제됨', id: 'Penerima dihapus' } as Record<string, string>)[language] || 'Recipient deleted');
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      const { data, error } = await supabase.functions.invoke('generate-auto-compose', {
        body: { recipientId },
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto-compose-queue'] });
      toast.success(
        language === 'en' 
          ? 'Message generated! Check pending approvals.' 
          : '¡Mensaje generado! Revisa las aprobaciones pendientes.'
      );
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to generate message');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-16 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!recipients?.length) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-12 text-center">
          <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">{t.noRecipients}</h3>
          <p className="text-muted-foreground">{t.noRecipientsDesc}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {recipients.map((recipient, index) => {
          const ContactIcon = contactIcons[recipient.contact_type] || Mail;
          const isActive = recipient.is_active && isEnabled;

          return (
            <motion.div
              key={recipient.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={!isActive ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium">{recipient.recipient_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ContactIcon className="h-3 w-3" />
                          <span>{recipient.contact_value}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <Badge variant={isActive ? 'default' : 'secondary'}>
                          {frequencyLabels[recipient.delivery_frequency]?.[language] || recipient.delivery_frequency}
                        </Badge>
                        {recipient.next_scheduled_at && isActive && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {t.nextSend}: {format(new Date(recipient.next_scheduled_at), 'MMM d, h:mm a')}
                          </p>
                        )}
                      </div>

                      <Badge variant={isActive ? 'outline' : 'secondary'}>
                        {isActive ? t.active : t.paused}
                      </Badge>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => generateMutation.mutate(recipient.id)}
                            disabled={generateMutation.isPending}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            {t.generateNow}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => toggleMutation.mutate({ id: recipient.id, isActive: !recipient.is_active })}
                          >
                            {recipient.is_active ? (
                              <>
                                <Pause className="h-4 w-4 mr-2" />
                                {t.pause}
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-2" />
                                {t.resume}
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => deleteMutation.mutate(recipient.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t.delete}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
